import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import Logo from "../components/brand/Logo";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";

export default function Login() {
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const setLang = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("eata.lang", lng);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("login.submit"));
      navigate(from, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 401) toast.error(t("login.invalidCreds"));
      else if (typeof detail === "string") toast.error(detail);
      else toast.error(t("login.genericError"));
    } finally {
      setLoading(false);
    }
  };

  const onGoogleLogin = () => {
    const callback = `${window.location.origin}/auth/google/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(callback)}`;
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex flex-col" data-testid="login-page">
      <div className="absolute top-6 right-6 text-xs flex items-center gap-1 rounded-lg bg-white border border-brand-lightGrey p-0.5 shadow-sm">
        <button
          data-testid="lang-en-login"
          onClick={() => setLang("en")}
          className={`px-2.5 py-1 rounded-md transition-colors ${i18n.language === "en" ? "bg-brand-navy text-white font-semibold" : "text-brand-mediumGrey hover:text-brand-navy"}`}
        >EN</button>
        <button
          data-testid="lang-es-login"
          onClick={() => setLang("es")}
          className={`px-2.5 py-1 rounded-md transition-colors ${i18n.language === "es" ? "bg-brand-navy text-white font-semibold" : "text-brand-mediumGrey hover:text-brand-navy"}`}
        >ES</button>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Logo variant="color" height={46} className="mx-auto" />
          </div>

          <div className="bg-white border border-brand-lightGrey rounded-2xl shadow-[0_8px_30px_rgba(10,31,68,0.06)] p-8" data-testid="login-card">
            <h1 className="font-heading text-2xl sm:text-[1.75rem]">
              {t("login.title")}
            </h1>
            <p className="text-sm text-brand-mediumGrey mt-2">{t("login.subtitle")}</p>

            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="email" className="label-eyebrow">{t("login.email")}</Label>
                <Input
                  id="email" type="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@english-academy.local"
                  className="mt-1.5"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password" className="label-eyebrow">{t("login.password")}</Label>
                <Input
                  id="password" type="password" required minLength={4}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5"
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit" disabled={loading}
                className="w-full bg-brand-red hover:bg-[#a50d25] text-white shadow-sm transition-colors"
                data-testid="login-submit-button"
              >
                {loading ? t("common.loading") : t("login.submit")}
              </Button>
            </form>

            <div className="my-6 flex items-center gap-3">
              <div className="h-px flex-1 bg-brand-lightGrey" />
              <span className="label-eyebrow text-brand-mediumGrey/70">{t("login.or")}</span>
              <div className="h-px flex-1 bg-brand-lightGrey" />
            </div>

            <Button
              variant="outline" onClick={onGoogleLogin}
              className="w-full border-brand-lightGrey text-brand-navy hover:bg-brand-offWhite"
              data-testid="login-google-button"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" className="mr-2">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t("login.google")}
            </Button>

            <p className="text-xs text-brand-mediumGrey/70 text-center mt-6">{t("login.footer")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
